/**
 * useRejectTask — calls complete-task with action='reject'.
 *
 * Reject requires a non-empty rejection_reason. The cleaner is notified
 * by the Edge Function (best-effort push via the notify function); the
 * room is moved back to a state the cleaner can re-engage with.
 *
 * Mirrors useApproveTask + useSubmitTask in shape so all three live
 * under the same error-mapping contract.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface RejectVars {
  taskId:          string
  rejectionReason: string
}

export interface RejectResult {
  task_id:          string
  task_status:      'rejected'
  room_next_status: string
  room: {
    room_id:         string
    previous_status: string
    new_status:      string
  }
}

export function useRejectTask(): UseMutationResult<RejectResult, AppError, RejectVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<RejectResult, AppError, RejectVars>({
    mutationFn: async ({ taskId, rejectionReason }): Promise<RejectResult> => {
      const trimmed = rejectionReason.trim()
      if (trimmed.length === 0) {
        throw new AppError({
          code:        'MISSING_REASON',
          message:     'rejection_reason is required',
          userMessage: 'Please add a reason for the rejection so the cleaner knows what to fix.',
          status:      400,
        })
      }

      const { data, error } = await supabase.functions.invoke<RejectResult>('complete-task', {
        body: {
          task_id:          taskId,
          action:           'reject',
          rejection_reason: trimmed,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REJECT_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Reject returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task',                vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['pending-inspections']              })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']                         })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'MISSING_FIELD':   return 'A rejection reason is required.'
    case 'INVALID_STATE':   return 'This task is not in a state that can be rejected.'
    case 'FORBIDDEN_ROLE':  return 'Only supervisors and above can reject tasks.'
    case 'FORBIDDEN_ORG':   return 'This task does not belong to your organization.'
    case 'RATE_LIMITED':    return 'Too many rejections in a row. Try again in a moment.'
    case 'TASK_NOT_FOUND':  return 'This task could not be found.'
    default:                return detail || 'Could not reject the task.'
  }
}
